/**
 * The app shell.
 *
 * Wayfinding is deliberately flat: two destinations, named for what is in them
 * rather than for where they sit ("Readiness", "Settings" — not "Home"). Every
 * screen can be left; nothing here traps the user.
 */

import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { useSession } from './hooks/useSession';
import { useVaultState } from './hooks/useVaultState';
import { Dashboard } from './routes/Dashboard';
import { Unlock } from './routes/Unlock';
import { bootstrapUser } from './lib/api';
import { DEMO } from './lib/devFlags';
import { reportDeviceState } from './lib/session';
import { classifyVaultStatus, isInstalledAsPWA, requestPersistence } from './vault/persistence';
import * as vault from './vault/vault';
import { LanguageProvider, useLanguage } from './i18n/context';

/*
 * Route-level splitting.
 *
 * The dashboard is what a reminder opens into, so it stays in the first chunk
 * along with sign-in and unlock. Everything else — the setup wizard, the anchor
 * form and its wordlists, the drill, restore, settings — is loaded on demand.
 * The seed-phrase guard pulls in two BIP-39 wordlists, which is a lot of bytes
 * to make someone download before they can see whether a drill is due.
 */
const Landing = lazy(() => import('./routes/Landing').then((m) => ({ default: m.Landing })));
const HowItWorks = lazy(() =>
  import('./routes/HowItWorks').then((m) => ({ default: m.HowItWorks })),
);
const Privacy = lazy(() => import('./routes/Privacy').then((m) => ({ default: m.Privacy })));
const VaultSetup = lazy(() =>
  import('./routes/VaultSetup').then((m) => ({ default: m.VaultSetup })),
);
const NewAnchor = lazy(() =>
  import('./routes/NewAnchor').then((m) => ({ default: m.NewAnchor })),
);
const Drill = lazy(() => import('./routes/Drill').then((m) => ({ default: m.Drill })));
const Restore = lazy(() => import('./routes/Restore').then((m) => ({ default: m.Restore })));
const Settings = lazy(() => import('./routes/Settings').then((m) => ({ default: m.Settings })));
const AnchorDetail = lazy(() =>
  import('./routes/AnchorDetail').then((m) => ({ default: m.AnchorDetail })),
);

export function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </LanguageProvider>
  );
}

function Shell() {
  const { user, loading, anchors, drills } = useSession();
  const vaultState = useVaultState();
  const { language } = useLanguage();

  // The launch heartbeat: ask for persistence, then tell the server whether the
  // vault is still here. `lost` is what turns the next reminder into "restore
  // from your file" instead of a drill the user cannot possibly pass.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    void (async () => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const locale = language || navigator.language || 'en';
      try {
        await bootstrapUser(timezone, locale);
      } catch {
        // Offline, or App Check has not warmed up. The dashboard still renders
        // from cache; the heartbeat retries on the next launch.
        return;
      }

      const persistence = await requestPersistence();
      const localIds = await vault.localAnchorIds();
      if (cancelled) return;

      if (DEMO) return;
      await reportDeviceState(user.uid, {
        localVaultStatus: classifyVaultStatus({
          hasLocalVault: localIds.length > 0 || vaultState.initialised,
          serverAnchorCount: anchors.length,
        }),
        persistGranted: persistence.persisted,
        installedAsPWA: isInstalledAsPWA(),
        timezone,
      }).catch(() => undefined);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, anchors.length, vaultState.initialised, language]);

  if (loading) return <Splash />;

  // Unauthenticated routes: Landing page, How It Works, Privacy, Restore
  if (!user) {
    return (
      <main className="min-h-dvh">
        <Suspense fallback={<Splash />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/restore" element={<Restore />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    );
  }

  // Authenticated routes
  return (
    <>
      {DEMO && <DemoBanner />}
      <main className="min-h-dvh">
        <Suspense fallback={<Splash />}>
          <Routes>
            <Route
              path="/"
              element={
                <Dashboard
                  anchors={anchors}
                  drills={drills}
                  unlocked={vaultState.strength !== 'locked'}
                />
              }
            />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/setup" element={<VaultSetup />} />
            <Route path="/restore" element={<Restore />} />
            <Route path="/unlock" element={<Unlock />} />
            <Route
              path="/anchors/new"
              element={
                vaultState.initialised ? (
                  vaultState.strength === 'locked' ? (
                    <Navigate to="/unlock" replace />
                  ) : (
                    <NewAnchor />
                  )
                ) : (
                  <Navigate to="/setup" replace />
                )
              }
            />
            <Route path="/anchors/:anchorId" element={<AnchorDetail anchors={anchors} />} />
            <Route
              path="/drill/:drillId"
              element={<Drill anchors={anchors} drills={drills} />}
            />
            <Route path="/settings" element={<Settings user={user} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <TabBar />
    </>
  );
}

/**
 * Translucent chrome with content scrolling underneath, not an opaque strip
 * that permanently costs the layout 4rem.
 */
function TabBar() {
  const { pathname } = useLocation();
  const { t } = useLanguage();

  // Hide tab bar on dedicated full-screen explanatory pages
  if (pathname === '/how-it-works' || pathname === '/privacy') {
    return null;
  }

  const tabs = [
    { to: '/', label: t.nav.readiness },
    { to: '/settings', label: t.nav.settings },
  ];

  return (
    <nav
      aria-label="Sections"
      className="material-chrome fixed inset-x-0 bottom-0 z-30 border-t pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="mx-auto flex max-w-lg">
        {tabs.map((tab) => {
          const active = pathname === tab.to;
          return (
            <li key={tab.to} className="flex-1">
              <Link
                to={tab.to}
                aria-current={active ? 'page' : undefined}
                className={`text-label flex h-14 items-center justify-center transition-colors duration-150 ${
                  active ? 'text-ink-7' : 'text-ink-4'
                }`}
              >
                {tab.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Impossible to mistake for the real thing, and impossible to reach in a
 * production build. Placed above the fold rather than tucked in a corner:
 * "is this real data?" should never be a question anyone has to ask.
 */
function DemoBanner() {
  return (
    <div className="sticky top-0 z-40 bg-due-soon/15 px-5 py-2 text-center backdrop-blur">
      <p className="text-label text-due-soon">
        Demo — no server. The vault, its encryption and the seed-phrase guard are real;
        schedules and scores live in this browser only.
      </p>
    </div>
  );
}

function Splash() {
  const { t } = useLanguage();
  return (
    <div className="grid min-h-dvh place-items-center">
      <span className="text-label text-ink-4">{t.common.loading}</span>
    </div>
  );
}
